import React, { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import PatientPicker from "@/components/PatientPicker";
import { X as XIcon, ShieldCheck, MapPin } from "lucide-react";

// Listino ufficiale (giu 2026): esattamente 10 modelli, nomi esatti, in quest'ordine.
export const SERVICES = [
  { id: "generale", label: "Ambulatorio Generale" },
  { id: "cataratta", label: "Ambulatorio Chirurgia della Cataratta" },
  { id: "postoperatorio", label: "Ambulatorio Controlli Postoperatori" },
  { id: "glaucoma", label: "Ambulatorio Glaucoma" },
  { id: "retina_medica", label: "Ambulatorio Retina Medica" },
  { id: "glaucoma_chirurgia", label: "Ambulatorio Chirurgia del Glaucoma" },
  { id: "ortottica", label: "Ambulatorio di Ortottica" },
  { id: "refrattiva", label: "Ambulatorio di Chirurgia Refrattiva" },
  { id: "fluorangiografia", label: "Fluorangiografia" },
  { id: "laser", label: "Ambulatorio Laser" },
];

// Etichette per service legacy/ritirati (visite storiche): mai riproposti nei selettori.
const LEGACY_SERVICE_LABELS = {
  base: "Visita generale / prima visita", visita: "Visita generale",
  glaucoma_visit: "Ambulatorio Glaucoma",
  retina: "Ambulatorio Retina Medica",
  cornea: "Cornea", rifrattiva: "Chirurgia rifrattiva", cornea_rifrattiva: "Chirurgia rifrattiva",
  strabismo: "Strabismo / Ortottica", strabismo_ortottica: "Ortottica",
  pediatrica: "Pediatrica", pediatrico: "Pediatrica",
  maculopatia: "Maculopatia", diabetica: "Retinopatia diabetica",
  annessi: "Annessi / Oculoplastica", oct: "OCT / Diagnostica",
  chirurgia: "Chirurgia (valutazione pre-operatoria)", urgenze: "Urgenze",
};

export const serviceLabel = (s) => (SERVICES.find((x) => x.id === s) || {}).label || LEGACY_SERVICE_LABELS[s] || s;

export default function NewVisitModal({ open, onClose, onCreated, sites = [], presetPatient = null }) {
  const [mode, setMode] = useState("existing");
  const [selected, setSelected] = useState(null);
  const [dupWarning, setDupWarning] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    patient_name: "", patient_surname: "", patient_birth: "", patient_fiscal_code: "",
    patient_phone: "", patient_email: "", patient_address: "", patient_city: "",
    patient_cap: "", patient_province: "", patient_notes: "", privacy_contact_consent: false,
    service: "generale", sede_id: "",
  });

  const fillFromPatient = (p) => ({
    patient_name: p.patient_name || "", patient_surname: p.patient_surname || "",
    patient_birth: p.patient_birth || "", patient_fiscal_code: p.patient_fiscal_code || "",
    patient_phone: p.patient_phone || "", patient_email: p.patient_email || "",
    patient_address: p.patient_address || "", patient_city: p.patient_city || "",
    patient_cap: p.patient_cap || "", patient_province: p.patient_province || "",
    patient_notes: p.patient_notes || "", privacy_contact_consent: !!p.privacy_contact_consent,
  });

  useEffect(() => {
    if (!open) return;
    if (presetPatient) {
      setMode("existing"); setSelected(presetPatient);
      setForm((f) => ({ ...f, ...fillFromPatient(presetPatient), service: "generale", sede_id: sites[0]?.id || "" }));
    } else {
      setMode("existing"); setSelected(null);
      setForm({ patient_name: "", patient_surname: "", patient_birth: "", patient_fiscal_code: "", patient_phone: "", patient_email: "", patient_address: "", patient_city: "", patient_cap: "", patient_province: "", patient_notes: "", privacy_contact_consent: false, service: "generale", sede_id: sites[0]?.id || "" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sites.length, presetPatient]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const handleSelect = (p) => {
    setSelected(p);
    if (p) setForm((f) => ({ ...f, ...fillFromPatient(p) }));
  };

  useEffect(() => {
    if (mode !== "new" || (!form.patient_surname.trim() && !form.patient_fiscal_code.trim())) { setDupWarning(null); return; }
    const t = setTimeout(async () => {
      try {
        const q = form.patient_fiscal_code.trim() || `${form.patient_name.trim()} ${form.patient_surname.trim()}`.trim();
        const { data } = await api.get("/medico/patients", { params: { q, limit: 5 } });
        const matches = (data || []).filter((p) =>
          (form.patient_fiscal_code && (p.patient_fiscal_code || "").toUpperCase() === form.patient_fiscal_code.toUpperCase()) ||
          ((p.patient_surname || "").toLowerCase() === form.patient_surname.trim().toLowerCase() && (p.patient_name || "").toLowerCase() === form.patient_name.trim().toLowerCase())
        );
        setDupWarning(matches.length ? matches[0] : null);
      } catch { setDupWarning(null); }
    }, 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, form.patient_name, form.patient_surname, form.patient_fiscal_code]);

  const create = async () => {
    if (creating) return;
    if (mode === "existing" && !selected) { toast.error("Seleziona un paziente o passa a 'Nuovo paziente'"); return; }
    if (!form.patient_name.trim() || !form.patient_surname.trim()) { toast.error("Nome e cognome obbligatori"); return; }
    setCreating(true);
    try {
      const { data } = await api.post("/visits", form);
      toast.success("Bozza creata");
      onCreated(data);
    } catch (e) { toast.error(apiErr(e, "Errore")); }
    finally { setCreating(false); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-xl p-6 max-w-2xl w-full my-8" onClick={(e) => e.stopPropagation()} data-testid="new-visit-modal">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-xl font-extrabold text-brand-navy">Nuova visita</h3>
          <button data-testid="new-visit-close" onClick={onClose}><XIcon /></button>
        </div>
        <p className="text-xs text-slate-500 mb-4">Privacy by design: per salvare la bozza bastano nome, cognome e tipologia. Gli altri campi sono consigliati per anagrafica completa e invio referto.</p>

        {!presetPatient && <PatientPicker mode={mode} setMode={setMode} selected={selected} onSelect={handleSelect} />}
        {presetPatient && (
          <div className="mb-3 bg-sky-50 border border-sky-200 rounded-lg p-3 text-sm" data-testid="new-visit-preset-patient">
            Paziente: <b className="text-brand-navy">{presetPatient.patient_name} {presetPatient.patient_surname}</b>
            {presetPatient.patient_birth ? ` · ${presetPatient.patient_birth}` : ""}
          </div>
        )}

        {mode === "new" && dupWarning && (
          <div className="mb-3 bg-amber-50 border border-amber-300 rounded-lg p-3 flex items-start gap-2 text-xs" data-testid="duplicate-patient-warning">
            <ShieldCheck size={14} className="text-amber-600 shrink-0 mt-0.5" />
            <div className="text-amber-800">
              <b>Possibile duplicato:</b> esiste già il paziente <b>{dupWarning.patient_name} {dupWarning.patient_surname}</b>
              {dupWarning.patient_birth ? ` (${dupWarning.patient_birth})` : ""}{dupWarning.patient_fiscal_code ? ` · CF ${dupWarning.patient_fiscal_code}` : ""}.
              <button type="button" data-testid="duplicate-use-existing" onClick={() => { setMode("existing"); handleSelect(dupWarning); }} className="ml-1 font-bold text-brand-azure underline">Usa il paziente esistente</button>
            </div>
          </div>
        )}

        {(mode === "new" || selected) && (
        <div className="space-y-4">
          <section>
            <div className="text-xs font-bold uppercase tracking-wider text-brand-navy mb-2">{selected ? "Anagrafica (autocompilata)" : "Anagrafica paziente"}</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-bold text-slate-500">Nome *</label>
                <input data-testid="new-visit-name" value={form.patient_name} onChange={(e) => setForm({ ...form, patient_name: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-[#E2E8F0]" />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500">Cognome *</label>
                <input data-testid="new-visit-surname" value={form.patient_surname} onChange={(e) => setForm({ ...form, patient_surname: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-[#E2E8F0]" />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500">Data nascita</label>
                <input type="date" data-testid="new-visit-birth" value={form.patient_birth} onChange={(e) => setForm({ ...form, patient_birth: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-[#E2E8F0]" />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500">Codice fiscale</label>
                <input data-testid="new-visit-cf" value={form.patient_fiscal_code} onChange={(e) => setForm({ ...form, patient_fiscal_code: e.target.value.toUpperCase() })} maxLength={16} className="mt-1 w-full px-3 py-2 rounded-lg border border-[#E2E8F0] uppercase" />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500">Telefono</label>
                <input data-testid="new-visit-phone" value={form.patient_phone} onChange={(e) => setForm({ ...form, patient_phone: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-[#E2E8F0]" />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500">Email</label>
                <input data-testid="new-visit-email" value={form.patient_email} onChange={(e) => setForm({ ...form, patient_email: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-[#E2E8F0]" />
              </div>
              <div className="col-span-2">
                <label className="text-xs font-bold text-slate-500">Indirizzo</label>
                <input data-testid="new-visit-address" value={form.patient_address} onChange={(e) => setForm({ ...form, patient_address: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-[#E2E8F0]" />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500">Città</label>
                <input data-testid="new-visit-city" value={form.patient_city} onChange={(e) => setForm({ ...form, patient_city: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-[#E2E8F0]" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold text-slate-500">CAP</label>
                  <input data-testid="new-visit-cap" value={form.patient_cap} onChange={(e) => setForm({ ...form, patient_cap: e.target.value })} maxLength={5} className="mt-1 w-full px-3 py-2 rounded-lg border border-[#E2E8F0]" />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-500">Prov.</label>
                  <input data-testid="new-visit-province" value={form.patient_province} onChange={(e) => setForm({ ...form, patient_province: e.target.value.toUpperCase() })} maxLength={2} className="mt-1 w-full px-3 py-2 rounded-lg border border-[#E2E8F0] uppercase" />
                </div>
              </div>
            </div>
          </section>

          <section>
            <div className="text-xs font-bold uppercase tracking-wider text-brand-navy mb-2">Tipologia visita</div>
            <select data-testid="new-visit-service" value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-[#E2E8F0]">
              {SERVICES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </section>

          {sites.length > 0 && (
            <section>
              <div className="text-xs font-bold uppercase tracking-wider text-brand-navy mb-2 flex items-center gap-1.5"><MapPin size={12} /> Sede / Studio</div>
              <div className="flex flex-wrap gap-2" data-testid="new-visit-sedi">
                {sites.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    data-testid={`new-visit-sede-${s.id}`}
                    onClick={() => setForm({ ...form, sede_id: s.id })}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border-2 text-xs font-bold transition ${form.sede_id === s.id ? "bg-brand-navy text-white border-brand-navy" : "bg-white text-slate-600 border-[#E2E8F0] hover:border-brand-azure"}`}
                  >
                    <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: s.color || "#2C4667" }}></span>
                    {s.name}
                  </button>
                ))}
                <button
                  type="button"
                  data-testid="new-visit-sede-none"
                  onClick={() => setForm({ ...form, sede_id: "" })}
                  className={`px-3 py-1.5 rounded-full border-2 text-xs font-bold transition ${!form.sede_id ? "bg-slate-100 text-slate-700 border-slate-300" : "bg-white text-slate-400 border-[#E2E8F0] hover:border-slate-300"}`}
                >Nessuna</button>
              </div>
            </section>
          )}

          <label className="flex items-start gap-2 bg-emerald-50 border border-emerald-200 rounded-lg p-3 cursor-pointer">
            <input data-testid="new-visit-privacy" type="checkbox" checked={form.privacy_contact_consent} onChange={(e) => setForm({ ...form, privacy_contact_consent: e.target.checked })} className="mt-1" />
            <div className="text-xs text-emerald-800">
              <div className="font-bold flex items-center gap-1.5"><ShieldCheck size={12} /> Consenso al contatto per invio referto</div>
              <div className="text-emerald-700 mt-0.5">Il paziente acconsente all'invio del referto via email/SMS in conformità al GDPR.</div>
            </div>
          </label>
        </div>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <button data-testid="new-visit-cancel" onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-100">Annulla</button>
          <button data-testid="confirm-new-visit" onClick={create} disabled={creating} className="bg-brand-navy hover:bg-brand-navyDark text-white font-bold px-5 py-2 rounded-lg disabled:opacity-60">{creating ? "Creazione…" : "Crea bozza"}</button>
        </div>
      </div>
    </div>
  );
}
